import { create } from 'zustand';
import agent from '../api/agent';
import { EventModel } from '../models/events/events';
import { createBaseStore } from './util/createBaseStore';
import { EntityStore } from './util/interfaces/entityStore';
import { byKeyResults, getOfflineDbTable, OfflineStoreKeys, upsertOfflineDbTableEntry } from '../offlinestore/offline.util';
import { Customer } from '../models/customers';
import useStandStore from '../store/standStore';
import { offlineStoreDb } from '../offlinestore/offlineStoreDb';


interface EventStore extends EntityStore<EventModel> {
    fetchEventsByStatus: (status: string) => Promise<EventModel[]>;
    eventsByStatus: EventModel[];
    customerEvents: EventModel[];
    fetchEventsByCustomerId: (customerId: string) => Promise<void>;
    downloadInvoiceByCustomerId: (customer: Customer, eventId: string, selectedStandType: string) => Promise<void>;
    downloadIssuedArticlesReportByCustomerId: (customer: Customer, eventId: string, selectedStandType: string) => Promise<void>;
    downloadIssuedEventArticlesByCustomerReport: (eventId: string, selectedStandType: string) => Promise<void>;
    downloadIssuedEventArticlesByProductReport: (eventId: string, selectedStandType: string) => Promise<void>;
    checklistData: { event: EventModel; customers: any[] } | null;
    fetchChecklistDataByEventId: (eventId: string) => Promise<void>;
}
export const useEventStore = create<EventStore>((set, get) => ({
    ...createBaseStore<EventModel>(agent.Events, OfflineStoreKeys.EVENTS)(set, get, {} as any),
    customerEvents: [],
    eventsByStatus: [],
    fetchEventsByCustomerId: async (customerId: string) => {
        set({ isLoading: true, error: null });
        try {
            const events = await agent.Events.getAllByCustomerId(customerId);
            set({ customerEvents: events });
        } catch (error) {
            set({ customerEvents: [] });
        } finally {
            set({ isLoading: false });
        }
    },
    checklistData: null,
    fetchEventsByStatus: async (status: string) => {
        set({ isLoading: true, error: null });
        try {
            const events = await agent.Events.getAllByStatus(status.toUpperCase());
            const offlineEvents = await byKeyResults(events, 'status', status.toLowerCase(), get().storeKey) as any;
            set({ eventsByStatus: navigator.onLine ? events : offlineEvents });
            return navigator.onLine ? events : offlineEvents;
        } catch (error) {
            const offlineEvents = await byKeyResults([], 'status', status.toLowerCase(), get().storeKey) as any;
            set({ eventsByStatus: offlineEvents });
        } finally {
            set({ isLoading: false });
        }
    },
    downloadInvoiceByCustomerId: async (customer: Customer, eventId: string, selectedStandType: string) => {
        try {
            const response = await agent.Events.downloadInvoiceByCustomerId(eventId, customer.id, selectedStandType);
            const blob = new Blob([response], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${customer.companyName!}-rechnung.pdf`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Error downloading PDF:", err);
        }
    },
    downloadIssuedArticlesReportByCustomerId: async (customer: Customer, eventId: string, selectedStandType: string) => {
        try {
            const response = await agent.Events.downloadIssuedArticlesReportByCustomerId(eventId, customer.id, selectedStandType);
            const blob = new Blob([response], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${customer.companyName!}-ausgegebene-artikel.pdf`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Error downloading PDF:", err);
        }
    },
    downloadIssuedEventArticlesByCustomerReport: async (eventId: string, selectedStandType: string) => {
        try {
            const response = await agent.Events.downloadIssuedEventArticlesByCustomerReport(eventId, selectedStandType);
            const blob = new Blob([response], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${get().selectedItem?.name}-ausgegebene-artikel.pdf`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Error downloading PDF:", err);
        }
    },
    async downloadIssuedEventArticlesByProductReport(eventId, selectedStandType) {
        try {
            const response = await agent.Events.downloadIssuedEventArticlesByProductReport(eventId, selectedStandType);
            const blob = new Blob([response], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${get().selectedItem?.name}-ausgegebene-artikel.pdf`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Error downloading PDF:", err);
        }
    },
    fetchChecklistDataByEventId: async (eventId: string) => {
        set({ isLoading: true, error: null });
        try {
            if (navigator.onLine) {
                const data = await agent.Events.getCheckListDataByEventId(eventId);
                set({ checklistData: data });
                useStandStore.getState().setStands(data.event?.stands || []);
                
                if (data.event) {
                    await upsertOfflineDbTableEntry(data.event, OfflineStoreKeys.EVENTS);
                }
                if (data.event?.stands) {
                    for (const stand of data.event.stands) {
                        await upsertOfflineDbTableEntry(stand, OfflineStoreKeys.STANDS);
                    }
                }
            } else {
                const cachedEvent = await offlineStoreDb.events.get(eventId);
                const cachedStands = await offlineStoreDb.stands.where('eventId').equals(eventId).toArray();
                const cachedCustomers = await offlineStoreDb.customers.toArray();
                
                if (cachedEvent) {
                    const standCustomerIds = [...new Set(cachedStands.map(s => s.customerId).filter(Boolean))];
                    const relevantCustomers = cachedCustomers.filter(c => standCustomerIds.includes(c.id));
                    
                    const checklistData = {
                        event: { ...cachedEvent, stands: cachedStands },
                        customers: relevantCustomers.map(customer => ({
                            ...customer,
                            stands: cachedStands.filter(s => s.customerId === customer.id)
                        }))
                    };
                    
                    set({ checklistData });
                    useStandStore.getState().setStands(cachedStands);
                } else {
                    set({ checklistData: null });
                }
            }
        } catch (error) {
            console.error('Error fetching checklist data:', error);
            
            try {
                const cachedEvent = await offlineStoreDb.events.get(eventId);
                const cachedStands = await offlineStoreDb.stands.where('eventId').equals(eventId).toArray();
                const cachedCustomers = await offlineStoreDb.customers.toArray();
                
                if (cachedEvent) {
                    const standCustomerIds = [...new Set(cachedStands.map(s => s.customerId).filter(Boolean))];
                    const relevantCustomers = cachedCustomers.filter(c => standCustomerIds.includes(c.id));
                    
                    const checklistData = {
                        event: { ...cachedEvent, stands: cachedStands },
                        customers: relevantCustomers.map(customer => ({
                            ...customer,
                            stands: cachedStands.filter(s => s.customerId === customer.id)
                        }))
                    };
                    
                    set({ checklistData });
                    useStandStore.getState().setStands(cachedStands);
                } else {
                    set({ checklistData: null });
                }
            } catch (offlineError) {
                console.error('Error loading offline checklist data:', offlineError);
                set({ checklistData: null });
            }
        } finally {
            set({ isLoading: false });
        }
    },
}));

export default useEventStore;
